import { leftOutNote } from "./overs";
import { counterText, NOT_RECORDED } from "./counters";
import { avgText, battingAverage, keptWicketEver, srText, toRateText } from "./recordFigures";

/**
 * The Dashboard's career figures (#1147, folding in #1044): every total
 * `CareerStatSections` shows (`pages/Statistics.jsx` and the coach snapshot),
 * as data. The numbers come from `GET /stats/summary` (`d`, filtered by
 * season/competition/team on the server); keeping is gated on
 * `GET /analytics/career`'s `wicketkeeping.matches`, the real
 * `kept_wicket` count (#704).
 *
 * A figure that was never recorded is a dash, not a nought (#715, R-005). A
 * RECORDED total of nothing is `0`, because nothing is the total. Keeping is
 * the one place this matters: a player who never kept wicket has no keeping
 * figures at all, so every keeping tile is a dash, never `0`.
 *
 * Formats follow D102/D184 (the rebuilt screens): strike rate one decimal,
 * economy two, averages one. The old Statistics screen keeps its own.
 *
 * @param {object} d  a `/stats/summary` payload
 * @param {object|null} wk  `/analytics/career`'s `wicketkeeping`
 * @param {object|null} [career]  the rest of `/analytics/career` (#1410, D367.18): the
 *   figures only the Record screen's old "Show more" drew (conversion, bowling average and
 *   strike rate, best figures, overs, keeping dismissals) join the tiles above when it is
 *   given. Without it the sections are exactly the board's.
 * @returns {{ key: string, title: string, note?: string|null, tiles: { key: string, label: string, value: string, restsOn: string|null, testId: string }[] }[]}
 */
export function careerSections(d, wk, career = null) {
  const tile = (key, label, value, restsOn = null) => ({ key, label, value, restsOn, testId: `insights-${key}` });
  // "Rests on" lines (the round-10 board): the base a rate stands on, said
  // only when there is one. An average with no dismissal has no "outs" to
  // rest on, a rate with no balls or overs none either.
  const outs = (d.innings ?? 0) - (d.not_outs ?? 0);
  // The API's `avg` is zero-filled (recordFigures.js header): decide from the counts.
  const average = battingAverage({ total_runs: d.total_runs, innings: d.innings, not_outs: d.not_outs }).value;
  const plural = (n, one) => `${n} ${one}${n === 1 ? "" : "s"}`;
  const outsLine = average != null && outs > 0 ? `Rests on ${plural(outs, "out")}` : null;
  const ballsLine = d.sr != null && d.total_balls_faced > 0 ? `Rests on ${plural(d.total_balls_faced, "ball")}` : null;
  // D260: the overs leave out spells with unreadable overs; say how many. A bowler
  // with only such spells has no economy: a dash, and the note alone.
  const leftOut = leftOutNote(Number(d.overs_left_out) || 0);
  const oversBase = d.econ != null && d.total_overs > 0 ? `Rests on ${plural(d.total_overs, "over")}` : null;
  const oversLine = leftOut ? [oversBase, `(${leftOut})`].filter(Boolean).join(" ") : oversBase;
  // The same note under the career call's overs-based tiles (D260), from that call's own count.
  const bowlLeftOut = leftOutNote(Number(career?.bowling?.overs_left_out) || 0);
  const bowlNote = bowlLeftOut ? `(${bowlLeftOut})` : null;
  const kept = keptWicketEver(wk);
  const keeping = (v) => (kept ? counterText(v) : NOT_RECORDED);
  // #1410: the union with Record's old "Show more". Each is added only when its source object is
  // there; an absent object is not a dash, it is a figure this call was not asked for.
  const bat = career?.batting;
  const bowl = career?.bowling;
  const extra = (obj, ...tiles) => (obj ? tiles : []);
  return [
    {
      key: "batting",
      title: "Career highlights",
      tiles: [
        tile("matches", "Matches", counterText(d.matches)),
        tile("innings", "Innings", counterText(d.innings)),
        tile("runs", "Runs", counterText(d.total_runs)),
        tile("not-outs", "Not outs", counterText(d.not_outs)),
        tile("avg", "Average", avgText(average), outsLine),
        tile("sr", "Strike rate", srText(d.sr), ballsLine),
        tile("highest", "Highest", counterText(d.highest)),
        tile("fifties", "50s", counterText(d.fifties)),
        tile("hundreds", "100s", counterText(d.hundreds)),
        tile("fours", "4s", counterText(d.fours)),
        tile("sixes", "6s", counterText(d.sixes)),
        tile("highlight-wickets", "Wickets", counterText(d.total_wickets)),
        ...extra(bat, tile("conversion", "Conversion", bat?.conversion_rate == null ? NOT_RECORDED : `${bat.conversion_rate}%`)),
      ],
    },
    {
      key: "bowling",
      title: "Bowling",
      tiles: [
        tile("wickets", "Wickets", counterText(d.total_wickets)),
        ...extra(bowl, tile("bowling-avg", "Bowling average", avgText(bowl?.avg))),
        tile("econ", "Economy", toRateText(d.econ), oversLine),
        ...extra(
          bowl,
          tile("bowling-sr", "Bowling strike rate", srText(bowl?.sr), bowlNote),
          tile("best-figures", "Best figures", counterText(bowl?.best_figures)),
          tile("matches-bowled", "Matches bowled", counterText(bowl?.matches_bowled)),
        ),
        tile("maidens", "Maidens", counterText(d.maidens)),
        tile("dot-balls", "Dot balls", counterText(d.dot_balls)),
        tile("wides", "Wides", counterText(d.wides)),
        tile("no-balls", "No balls", counterText(d.no_balls)),
      ],
    },
    {
      key: "fielding",
      title: "Fielding",
      tiles: [
        tile("catches", "Catches", counterText(d.catches)),
        tile("run-outs", "Run outs", counterText(d.run_outs)),
        tile("direct-hits", "Direct hits", counterText(d.direct_hit_run_outs)),
        tile("assists", "Assists", counterText(d.assists)),
      ],
    },
    {
      key: "keeping",
      title: "Keeping",
      note: kept ? null : "A dash, not a nought: you haven't kept wicket.",
      tiles: [
        tile("kept", "Matches kept", keeping(wk?.matches)),
        tile("wk-catches", "Catches", keeping(wk?.catches)),
        tile("stumpings", "Stumpings", keeping(d.stumpings)),
        tile("byes", "Byes conceded", keeping(d.byes_conceded)),
        tile("wk-run-outs", "Run outs", keeping(d.wicketkeeping_run_outs)),
        tile("wk-assists", "Assists", keeping(d.wicketkeeping_assists)),
        ...extra(
          wk && career,
          tile("wk-dismissals", "Total dismissals", keeping(wk?.dismissals), kept ? "Catches and stumpings" : null),
          tile("wk-dismissals-per-match", "Dismissals per match", keeping(wk?.dismissals_per_match)),
        ),
      ],
    },
  ];
}
